import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TradingCounterParty } from "../../../../api/Api";
import dayjs from "dayjs";
import { mergerSummaryRows } from "../../../mergers/mergerSummaryRows";
import { operationalReadinessPdfRows } from "../../../../components/lifecycle/signOff/operationalReadiness";

type Row = [string, string];

const fmt = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const section = (
  doc: jsPDF,
  title: string,
  rows: Row[],
  startY: number
): number => {
  autoTable(doc, {
    head: [[title, ""]],
    body: rows.filter((row) => row[0] !== "" && row[1] !== ""),
    startY,
    theme: "grid",
    headStyles: { fillColor: [18, 46, 56], textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: "bold" },
      1: { cellWidth: 110 }
    }
  });
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;
  return finalY + 6;
};

export const buildSignOffPdf = (counterparty: TradingCounterParty): jsPDF => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const margin = 14;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Counterparty: ${counterparty.counterpartyName || ""}${counterparty.isMerger ? " (MERGER)" : ""}`,
    margin,
    margin + 4
  );

  let y = margin + 12;

  y = section(
    doc,
    "Basic Information",
    [
      ["Counterparty type", fmt(counterparty.counterpartyType)],
      ["Trading team", fmt(counterparty.tradingTeam)],
      ["Contracting Entity", fmt(counterparty.contractingEntity)],
      ["Product Type", fmt(counterparty.productType)],
      ["Venue", fmt(counterparty.venue)],
      ["Settlement type", fmt(counterparty.tradingSettlementType)],
      ["Agreement type", fmt(counterparty.agreementType)],
      ["Transaction type", fmt(counterparty.transactionType)],
      ["Mandate granted", fmt(counterparty.mandate)],
      ["Deal Unit", fmt(counterparty.unit)],
      [
        "Expected deal volume/size",
        counterparty.dealVolume
          ? `${counterparty.dealVolume} ${counterparty.unit || ""}`
          : "N/A"
      ],
      counterparty.marketAccessLifecycle?.signOffNote
        ? [
            "Sign-off note",
            fmt(counterparty.marketAccessLifecycle?.signOffNote)
          ]
        : ["", ""]
    ],
    y
  );

  if (counterparty.isMerger) {
    y = section(
      doc,
      "Merger",
      mergerSummaryRows(
        "counterparty",
        counterparty.mergerDescription,
        counterparty.marketAccessLifecycle
      ),
      y
    );
  }

  const compliance = counterparty.complianceLifecycle;
  y = section(
    doc,
    "Compliance",
    [
      ["KYC & Sanction screening", fmt(compliance?.kycCheck)],
      ["KYC Check on MFT", fmt(compliance?.kycCheckMft)],
      compliance?.signOffNote
        ? ["Sign-off note", fmt(compliance?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const legal = counterparty.legalLifecycle;
  y = section(
    doc,
    "Legal",
    [
      ["Agreement review", fmt(legal?.agreementReview)],
      ["NDA Review", fmt(legal?.ndaReview)],
      legal?.signOffNote ? ["Sign-off note", fmt(legal?.signOffNote)] : ["", ""]
    ],
    y
  );

  const risk = counterparty.riskLifecycle;
  y = section(
    doc,
    "Risk",
    [
      ["Mandate", fmt(risk?.mandateApproval)],
      risk?.signOffNote ? ["Sign-off note", fmt(risk?.signOffNote)] : ["", ""]
    ],
    y
  );

  const creditRisk = counterparty.creditRiskLifecycle;
  y = section(
    doc,
    "Credit Risk",
    [
      ["Credit check", fmt(creditRisk?.creditCheck)],
      ["Credit amount", fmt(creditRisk?.creditAmount)],
      ["Credit currency", fmt(creditRisk?.creditCurrency)],
      creditRisk?.signOffNote
        ? ["Sign-off note", fmt(creditRisk?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const treasury = counterparty.treasuryLifecycle;
  y = section(
    doc,
    "Treasury",
    [
      ["Agreement review", fmt(treasury?.agreementReview)],
      ["Collateral form", fmt(treasury?.collateralForm)],
      ["Letter of Credit Status", fmt(treasury?.collateralFormStatus)],
      ["Amount", fmt(treasury?.amount)],
      ["Currency", fmt(treasury?.currency)],
      ["Contact person", fmt(treasury?.contactPerson)],
      ["Cash flow forecast", fmt(treasury?.cashFlowForecast)],
      treasury?.signOffNote
        ? ["Sign-off note", fmt(treasury?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const finance = counterparty.financeLifecycle;
  y = section(
    doc,
    "Finance",
    [
      ["Agreement review", fmt(finance?.agreementReview)],
      [
        "Counterparty setup in Business Central",
        fmt(finance?.counterpartySetup)
      ],
      ["Test trade required for settlement method", fmt(finance?.testTrade)],
      ["IBAN", fmt(finance?.iban)],
      ["Callback", fmt(finance?.callback)],
      finance?.signOffNote
        ? ["Sign-off note", fmt(finance?.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const tech = counterparty.techLifecycle;
  y = section(
    doc,
    "Technology",
    [
      [
        "Included in existing trade capture",
        fmt(tech?.includedInExistingTradeCapture)
      ],
      ["New trade capture developed status", fmt(tech?.newTradeCaptureStatus)],
      tech?.signOffNote ? ["Sign-off note", fmt(tech?.signOffNote)] : ["", ""]
    ],
    y
  );

  const signOff = counterparty.signOff;
  const readinessRows = operationalReadinessPdfRows(
    signOff?.operationalReadiness
  );
  if (readinessRows) {
    y = section(doc, "Operational readiness", readinessRows, y);
  }

  if (signOff?.summary) {
    y = section(doc, "Summary", [["", signOff.summary]], y);
  }

  if (signOff?.signatories?.length) {
    const statusRows: Row[] = [["Status", fmt(signOff.status)]];
    if (signOff.status === "rejected") {
      statusRows.push(["Rejected by", fmt(signOff.rejectedBy)]);
      if (signOff.rejectedAt) {
        statusRows.push([
          "Rejected at",
          dayjs(signOff.rejectedAt).format("LLL")
        ]);
      }
      statusRows.push(["Rejection reason", fmt(signOff.rejectionReason)]);
    }
    const signatoryRows: Row[] = signOff.signatories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => [
        `${s.name}${s.isCeo ? " (CEO)" : ""}`,
        s.signed
          ? `Signed${s.signedAt ? ` on ${dayjs(s.signedAt).format("LLL")}` : ""}`
          : "Pending"
      ]);
    section(doc, "Sign off", [...statusRows, ...signatoryRows], y);
  }

  return doc;
};
